import React, { useState } from 'react';

const IntegratedAreas = () => {
  const [activeArea, setActiveArea] = useState(0);

  const areas = [
    {
      id: 1,
      title: 'INTERIOR DESIGN',
      subtitle: 'Creative Concepts & Detailed Planning',
      description: 'We initiate the journey by developing creative concepts, detailed space planning, and specifying all materials, furniture, fixtures, and lighting to create stunning and functional interiors.',
      highlights: [
        'Creative concept development',
        'Detailed space planning',
        'Material specification',
        'Furniture & fixture selection',
        'Lighting design'
      ],
      image: '/img/img24.jpg',
      color: '#1B211A',
      icon: '✨'
    },
    {
      id: 2,
      title: 'RENOVATION & REMODELING',
      subtitle: 'Transform Existing Spaces',
      description: 'We revitalize existing spaces by undertaking comprehensive structural and aesthetic upgrades. From kitchen and bathroom modernization to full floor-plan reconfiguration, we give tired properties a new lease on life.',
      highlights: [
        'Structural upgrades',
        'Kitchen & bathroom modernization',
        'Floor-plan reconfiguration',
        'Aesthetic transformation',
        'Quality material selection'
      ],
      image: '/img/img21.jpg',
      color: '#1B211A',
      icon: '🔨'
    },
    {
      id: 3,
      title: 'INTERIOR FIT-OUT',
      subtitle: 'Technical Construction Excellence',
      description: 'We execute the technical construction work for new or renovated spaces, managing all aspects of MEP (mechanical, electrical, plumbing), carpentry, flooring, ceilings, and finishing with exceptional quality control.',
      highlights: [
        'MEP installation',
        'Custom carpentry',
        'Flooring & ceiling works',
        'Quality assurance',
        'Project management'
      ],
      image: '/img/img13.jpg',
      color: '#1B211A',
      icon: '⚙️'
    },
    {
      id: 4,
      title: 'LANDSCAPING',
      subtitle: 'Sustainable Outdoor Design',
      description: 'We complete the project scope by designing and building beautiful, sustainable, and functional outdoor areas, ensuring the exterior of your property perfectly complements its interior.',
      highlights: [
        'Sustainable design',
        'Outdoor space planning',
        'Garden & hardscape design',
        'Irrigation systems',
        'Maintenance planning'
      ],
      image: '/img/img14.jpg',
      color: '#1B211A',
      icon: '🌿'
    }
  ];

  const contactInfo = {
    title: "Let's Bring Your Dream Space to Life",
    subtitle: "Work with New Galaxy to create a home that reflects you.",
    email: "gm@newgalaxyy.com",
    phone1: "+971 563252389",
    location:"Abudhabi,Uae"
  };

  return (
    <div className="integrated-areas">
      {/* Decorative Background Elements */}
      <div className="area-bg-pattern"></div>
      <div className="area-floating-shapes">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="floating-shape" style={{ '--delay': `${i}s` }}></div>
        ))}
      </div>

      <div className="container-xxl">
        {/* Main Header */}
        <div className="area-header">
          <div className="header-badge">
            <span>INTEGRATED SOLUTIONS</span>
            <div className="badge-dot"></div>
          </div>
          <h1 className="area-main-title">
            <span className="title-line">OUR INTEGRATED</span>
            <span className="title-line highlight">FOUR KEY AREAS</span>
          </h1>
          <p className="area-subtitle">
            A comprehensive approach to transforming spaces from vision to reality
          </p>
        </div>

        <div className="areas-container">
          {/* Left Column - Area Selection */}
          <div className="areas-navigation">
            <div className="nav-header">
              <h3 className="nav-title">Key Areas</h3>
              <p className="nav-subtitle">Select an area to explore</p>
            </div>
            
            <div className="area-buttons">
              {areas.map((area, index) => (
                <button
                  key={area.id}
                  className={`area-btn ${activeArea === index ? 'active' : ''}`}
                  onClick={() => setActiveArea(index)}
                  style={{
                    '--area-color': area.color,
                    '--area-bg': `${area.color}15`
                  }}
                >
                  <div className="btn-content">
                    <span className="btn-icon">{area.icon}</span>
                    <div className="btn-text">
                      <span className="btn-title">{area.title}</span>
                      <span className="btn-subtitle">{area.subtitle}</span>
                    </div>
                  </div>
                  <div className="btn-indicator"></div>
                </button>
              ))}
            </div>

            {/* Contact Card */}
            <div className="contact-card">
              <h4 className="contact-card-title">{contactInfo.title}</h4>
              <p className="contact-card-subtitle">{contactInfo.subtitle}</p>
              <div className="contact-details">
                <div className="contact-row">
                  <span className="contact-label">Email:</span>
                  <span className="contact-value">{contactInfo.email}</span>
                </div>
               
                <div className="contact-row">
                  <span className="contact-label">Phone:</span>
                  <span className="contact-value">{contactInfo.phone1}</span>
                </div>
                <div className="contact-row">
                  <span className="contact-label">Location:</span>
                  <span className="contact-value">{contactInfo.location}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Area Details */}
          <div className="area-details">
            <div className="details-card">
              {/* Area Header */}
              <div className="details-header">
                <div 
                  className="area-color-marker"
                  style={{ backgroundColor: areas[activeArea].color }}
                ></div>
                <div>
                  <h2 className="area-detail-title">{areas[activeArea].title}</h2>
                  <p className="area-detail-subtitle">{areas[activeArea].subtitle}</p>
                </div>
              </div>

              {/* Content Split */}
              <div className="details-content">
                {/* Left: Description & Highlights */}
                <div className="description-section">
                  <div className="description-text">
                    <p>{areas[activeArea].description}</p>
                  </div>
                  
                  <div className="highlights-section">
                    <h4 className="highlights-title">Key Highlights</h4>
                    <ul className="highlights-list">
                      {areas[activeArea].highlights.map((item, index) => (
                        <li key={index} className="highlight-item">
                          <span className="highlight-bullet" style={{ color: areas[activeArea].color }}>
                            ●
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Right: Image with Overlay */}
                <div className="image-section">
                  <div className="image-wrapper">
                    <img 
                      src={areas[activeArea].image} 
                      alt={areas[activeArea].title}
                      className="area-image"
                    />
                    <div className="image-overlay">
                      <div className="overlay-content">
                        <span className="overlay-badge" style={{ backgroundColor: areas[activeArea].color }}>
                          {areas[activeArea].icon}
                        </span>
                        <div className="overlay-text">
                          <span className="overlay-title">Visual Preview</span>
                          <span className="overlay-subtitle">Sample of our work</span>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="image-glow-effect"
                      style={{ '--glow-color': `${areas[activeArea].color}40` }}
                    ></div>
                  </div>

                  {/* Interactive Elements */}
                  <div className="interactive-elements">
                    <button className="interactive-btn">
                      <span className="btn-icon-small">📐</span>
                      <span>View 3D Plan</span>
                    </button>
                    <button className="interactive-btn">
                      <span className="btn-icon-small">🎨</span>
                      <span>Material Options</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Progress Indicator */}
              <div className="area-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ 
                      width: `${(activeArea + 1) * 25}%`,
                      backgroundColor: areas[activeArea].color
                    }}
                  ></div>
                </div>
                <div className="progress-labels">
                  <span>Areas of Expertise</span>
                  <span>{activeArea + 1} of {areas.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegratedAreas;